import { useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const inputClass =
  'border-[rgba(6,43,26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

function getCellValue(row, column) {
  if (typeof column.accessor === 'function') return column.accessor(row);
  if (typeof column.accessor === 'string') return row[column.accessor];
  return row[column.id];
}

function compareValues(a, b) {
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : -1;
  if (b === null || b === undefined) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export default function DataTable({
  title,
  description,
  data = [],
  columns = [],
  getRowId = (row) => row.id,
  loading = false,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchHint,
  filters = [],
  selectedIds,
  onSelectedIdsChange,
  bulkActions,
  actionsBelowSearch = false,
  rowActions,
  onRowClick,
  emptyMessage = 'No records found.',
  pageSize = 10,
  footerSummary,
  showPagination = true,
}) {
  const [sort, setSort] = useState({ id: columns.find((column) => column.defaultSort)?.id || '', direction: 'asc' });
  const [page, setPage] = useState(1);

  const selectable = selectedIds && onSelectedIdsChange;

  const sortedData = useMemo(() => {
    if (!sort.id) return data;
    const column = columns.find((item) => item.id === sort.id);
    if (!column) return data;
    const direction = sort.direction === 'desc' ? -1 : 1;
    return [...data].sort((left, right) => {
      const a = column.sortAccessor ? column.sortAccessor(left) : getCellValue(left, column);
      const b = column.sortAccessor ? column.sortAccessor(right) : getCellValue(right, column);
      return compareValues(a, b) * direction;
    });
  }, [columns, data, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedData = sortedData.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pageIds = pagedData.map(getRowId);
  const selectedCount = selectedIds?.size || 0;
  const allPageSelected = selectable && pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = selectable && !allPageSelected && pageIds.some((id) => selectedIds.has(id));

  const setSelected = (updater) => {
    onSelectedIdsChange((prev) => {
      const current = prev instanceof Set ? prev : new Set(prev || []);
      return updater(current);
    });
  };

  const toggleRowSelected = (rowId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const togglePageSelected = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const updateSort = (column) => {
    if (!column.sortable) return;
    setSort((prev) => {
      if (prev.id !== column.id) return { id: column.id, direction: 'asc' };
      return { id: column.id, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const SortIcon = ({ column }) => {
    if (!column.sortable) return null;
    if (sort.id !== column.id) return <ChevronsUpDown className="w-3.5 h-3.5 text-[#062B1A]/35" />;
    return sort.direction === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 text-[#D4AF37]" />
      : <ChevronDown className="w-3.5 h-3.5 text-[#D4AF37]" />;
  };

  return (
    <div className="space-y-4">
      {(title || description || onSearchChange || filters.length > 0 || bulkActions) && (
        <div className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-white p-4 shadow-sm space-y-4">
          {(title || description) && (
            <div>
              {title && (
                <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                  {title}
                </h3>
              )}
              {description && <p className="mt-1 text-sm text-[#062B1A]/55">{description}</p>}
            </div>
          )}

          <div className={cn('flex gap-3', actionsBelowSearch ? 'flex-col' : 'flex-wrap items-center justify-between')}>
            <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
              {onSearchChange && (
                <div className={cn('relative flex-1 min-w-[240px]', !actionsBelowSearch && 'max-w-xl')}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#062B1A]/40" />
                  <Input
                    type="search"
                    aria-label={searchPlaceholder || 'Search'}
                    value={searchValue}
                    onChange={(event) => {
                      setPage(1);
                      onSearchChange(event.target.value);
                    }}
                    placeholder={searchPlaceholder}
                    className={cn('pl-9 [&::-webkit-search-cancel-button]:appearance-none', searchValue && 'pr-9', inputClass)}
                  />
                  {searchValue && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-[#062B1A]/45 transition-colors hover:bg-[#062B1A]/5 hover:text-[#062B1A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50"
                      onClick={() => {
                        setPage(1);
                        onSearchChange('');
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
              {filters.map((filter) => (
                <div key={filter.id} className="w-full md:w-[190px] md:shrink-0">
                  <Select
                    value={filter.value}
                    onValueChange={(value) => {
                      setPage(1);
                      filter.onChange(value);
                    }}
                  >
                    <SelectTrigger className={inputClass}>
                      <SelectValue placeholder={filter.placeholder || filter.label} />
                    </SelectTrigger>
                    <SelectContent>
                      {filter.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {(bulkActions || selectedCount > 0) && (
              <div className="flex flex-wrap items-center gap-2">
                {(selectedCount > 0 || !actionsBelowSearch) && (
                  <p className="text-xs text-[#062B1A]/55">
                    {selectedCount > 0 ? `${selectedCount} selected` : 'Select rows for bulk actions'}
                  </p>
                )}
                {selectedCount > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-[#062B1A]/15 text-[#062B1A]"
                    onClick={() => onSelectedIdsChange(new Set())}
                  >
                    Clear
                  </Button>
                )}
                {typeof bulkActions === 'function' ? bulkActions({ selectedIds, selectedCount }) : bulkActions}
              </div>
            )}
          </div>

          {onSearchChange && searchHint && (
            <p className="text-xs leading-relaxed text-[#062B1A]/45">{searchHint}</p>
          )}
        </div>
      )}

      <div className="rounded-xl border border-[rgba(6,43,26,0.08)] bg-white shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[rgba(6,43,26,0.04)] hover:bg-[rgba(6,43,26,0.04)]">
              {selectable && (
                <TableHead className="w-[44px]">
                  <Checkbox
                    checked={allPageSelected ? true : somePageSelected ? 'indeterminate' : false}
                    onCheckedChange={togglePageSelected}
                    disabled={pageIds.length === 0}
                    aria-label="Select visible rows"
                  />
                </TableHead>
              )}
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  className={cn('text-[#062B1A] font-semibold', column.headerClassName)}
                >
                  <button
                    type="button"
                    disabled={!column.sortable}
                    onClick={() => updateSort(column)}
                    className={cn(
                      'inline-flex items-center gap-1.5 text-left',
                      column.sortable ? 'hover:text-[#D4AF37]' : 'cursor-default',
                    )}
                  >
                    {column.header}
                    <SortIcon column={column} />
                  </button>
                </TableHead>
              ))}
              {rowActions && <TableHead className="text-right text-[#062B1A] font-semibold">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)}>
                    <div className="h-10 rounded bg-[#F7F8FA] animate-pulse" />
                  </TableCell>
                </TableRow>
              ))
            ) : pagedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)} className="py-12 text-center text-sm text-[#062B1A]/45">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              pagedData.map((row) => {
                const rowId = getRowId(row);
                return (
                  <TableRow
                    key={rowId}
                    className={cn('table-row-hover align-top', onRowClick && 'cursor-pointer')}
                    onClick={() => onRowClick?.(row)}
                  >
                    {selectable && (
                      <TableCell className="w-[44px]" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(rowId)}
                          onCheckedChange={() => toggleRowSelected(rowId)}
                          aria-label={`Select row ${rowId}`}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell key={column.id} className={column.cellClassName}>
                        {column.cell ? column.cell(row) : getCellValue(row, column)}
                      </TableCell>
                    ))}
                    {rowActions && (
                      <TableCell className="text-right" onClick={(event) => event.stopPropagation()}>
                        {rowActions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {(footerSummary || showPagination) && (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-[#062B1A]/60">
        <span>
          {footerSummary || (
            <>
              Showing {sortedData.length === 0 ? 0 : (safePage - 1) * pageSize + 1}
              {' - '}
              {Math.min(safePage * pageSize, sortedData.length)}
              {' of '}
              {sortedData.length}
            </>
          )}
        </span>
        {showPagination && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={safePage <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="border-[#062B1A]/15 text-[#062B1A]"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <span className="text-xs font-medium text-[#062B1A]/50">
            Page {safePage} of {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={safePage >= totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            className="border-[#062B1A]/15 text-[#062B1A]"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        )}
      </div>
      )}
    </div>
  );
}
